import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Truck, Check } from "lucide-react";
import SEO from "@/components/SEO";

const MANUFACTURING = [
  "Private label collaboration",
  "Consistent production opportunities",
  "Long-term B2B growth",
  "Premium dessert portfolio",
];

const DISTRIBUTION = [
  "Cold-chain distribution opportunities",
  "Hospitality-focused routes",
  "Regional expansion potential",
  "Reliable reorder model",
];

export default function Partnerships() {
  return (
    <div data-testid="partnerships-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16">
      <SEO
        title="Partnerships — Manufacturers & distributors"
        description="Partner with Cake World USA for private-label manufacturing or cold-chain distribution across the United States."
      />
      <div className="max-w-3xl">
        <div className="eyebrow">Partnerships</div>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl text-brand-ink leading-none">Grow with Cake World.</h1>
        <p className="mt-6 text-brand-stone leading-relaxed text-lg font-ui">
          We work with two types of B2B partners — manufacturers and distributors — to bring premium desserts to hospitality businesses across the country.
        </p>
      </div>

      <div className="dot-divider mt-14" />

      <div className="mt-14 grid md:grid-cols-2 gap-8">
        <div data-testid="manufacturing-partner-card" className="bg-white border border-brand-line rounded-3xl p-10 shadow-soft">
          <div className="w-14 h-14 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center">
            <Package className="w-6 h-6" />
          </div>
          <h2 className="mt-6 font-display text-3xl text-brand-ink">For Manufacturing Partners</h2>
          <p className="mt-3 text-brand-stone font-ui">
            Collaborate with Cake World on private-label production. We supply the recipes, brand and demand — you supply the capacity.
          </p>
          <ul className="mt-6 space-y-3 text-sm font-ui text-brand-ink">
            {MANUFACTURING.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-brand-red mt-0.5 shrink-0" /> <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contact?type=manufacturer"
            data-testid="apply-manufacturing"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition"
          >
            Apply as manufacturer <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div data-testid="distribution-partner-card" className="bg-white border border-brand-line rounded-3xl p-10 shadow-soft">
          <div className="w-14 h-14 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center">
            <Truck className="w-6 h-6" />
          </div>
          <h2 className="mt-6 font-display text-3xl text-brand-ink">For Distribution Partners</h2>
          <p className="mt-3 text-brand-stone font-ui">
            Join our hospitality-focused distribution network. We provide the products and reorder model — you own the route and relationships.
          </p>
          <ul className="mt-6 space-y-3 text-sm font-ui text-brand-ink">
            {DISTRIBUTION.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-brand-red mt-0.5 shrink-0" /> <span>{b}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/contact?type=distributor"
            data-testid="apply-distribution"
            className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white text-sm font-ui font-semibold hover:bg-brand-redDark transition"
          >
            Apply as distributor <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <section className="mt-24 bg-brand-pinkSoft rounded-3xl border border-brand-line p-10 md:p-14 shadow-soft grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="eyebrow">Partnership process</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-brand-ink leading-tight">How the partnership works.</h2>
          <p className="mt-4 text-brand-stone font-ui">
            We keep things simple. Every partnership begins with a 30-minute discovery call — no commitments — where we align on capacity, geography and business goals.
          </p>
        </div>
        <ol className="space-y-4">
          {[
            "Introductory discovery call",
            "Category & capacity assessment",
            "Commercial proposal & terms",
            "Onboarding and first order",
          ].map((s, i) => (
            <li key={s} className="flex items-start gap-4 bg-white rounded-2xl border border-brand-line p-4">
              <div className="font-display text-2xl text-brand-red w-8">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-brand-ink font-ui">{s}</div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
